import React from 'react';

const toNumber = (raw, fallback = 0) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : fallback;
  if (raw === null || raw === undefined) return fallback;
  const cleaned = `${raw}`.replace(/[$,%\s]/g, '').replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : fallback;
};

function EditModal({ trade, onSave, onClose, C, inp, cardStyle }) {
  const [form, setForm] = React.useState({
    date: trade.date || '',
    asset: trade.asset || '',
    market: trade.market || 'Stocks',
    type: trade.type || 'Buy',
    strategy: trade.strategy || '',
    entry: trade.entry || '',
    exit: trade.exit || '',
    size: trade.size || '',
    stopLoss: trade.stopLoss || '',
    target: trade.target || '',
    commission: trade.commission || '',
    tags: trade.tags || '',
    notes: trade.notes || '',
    emotion: trade.emotion || '',
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = () => {
    const rawPnl = (toNumber(form.exit) - toNumber(form.entry)) * toNumber(form.size) * (form.type === 'Buy' ? 1 : -1);
    const commission = toNumber(form.commission, 0);
    const pnl = (rawPnl - commission).toFixed(2);
    onSave({ ...form, pnl });
  };

  const fieldStyle = { ...inp, width: '100%', boxSizing: 'border-box' };
  const labelStyle = { color: C.sub, fontSize: '11px', fontWeight: '600', marginBottom: '4px', display: 'block' };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={onClose}>
      <div style={{ ...cardStyle, padding: '24px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '12px' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ color: C.text, fontSize: '16px', fontWeight: '800' }}>Edit Trade</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.sub, fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={row2}>
            <div>
              <label style={labelStyle}>Date</label>
              <input type="date" value={form.date} onChange={set('date')} style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Asset</label>
              <input value={form.asset} onChange={set('asset')} style={fieldStyle} />
            </div>
          </div>

          <div style={row2}>
            <div>
              <label style={labelStyle}>Market</label>
              <select value={form.market} onChange={set('market')} style={fieldStyle}>
                {['Stocks','Options','Futures','Forex','Crypto'].map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={form.type} onChange={set('type')} style={fieldStyle}>
                <option>Buy</option><option>Sell</option>
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Strategy</label>
            <select value={form.strategy} onChange={set('strategy')} style={fieldStyle}>
              <option value="">— None —</option>
              {['Breakout','Scalp','Swing','Trend Following','Reversal'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>

          <div style={row2}>
            <div>
              <label style={labelStyle}>Entry Price</label>
              <input type="number" value={form.entry} onChange={set('entry')} style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Exit Price</label>
              <input type="number" value={form.exit} onChange={set('exit')} style={fieldStyle} />
            </div>
          </div>

          <div style={row2}>
            <div>
              <label style={labelStyle}>Size / Qty</label>
              <input type="number" value={form.size} onChange={set('size')} style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Commission ($)</label>
              <input type="number" value={form.commission} onChange={set('commission')} style={fieldStyle} />
            </div>
          </div>

          <div style={row2}>
            <div>
              <label style={labelStyle}>Stop Loss</label>
              <input type="number" value={form.stopLoss} onChange={set('stopLoss')} style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Target</label>
              <input type="number" value={form.target} onChange={set('target')} style={fieldStyle} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Tags (comma-separated)</label>
            <input value={form.tags} onChange={set('tags')} placeholder="e.g. FOMO, revenge, planned" style={fieldStyle} />
          </div>

          <div>
            <label style={labelStyle}>Emotion</label>
            <input value={form.emotion} onChange={set('emotion')} style={fieldStyle} />
          </div>

          <div>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={set('notes')} rows={3} style={{ ...fieldStyle, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.card2, color: C.text, cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
            <button onClick={handleSave} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: C.blue, color: '#fff', cursor: 'pointer', fontWeight: '700' }}>Save Changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TradeTable({
  accountTrades,
  filteredTrades,
  filterAsset,
  setFilterAsset,
  filterStrategy,
  setFilterStrategy,
  setSelectedTradeDetail,
  openReplay,
  deleteTrade,
  editTrade,
  bulkDeleteTrades,
  formatTradeRr,
  resolveTvSymbol,
  isMobile,
  C,
  cardStyle,
  inp,
  PnLSummaryBar,
}) {
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [page, setPage] = React.useState(1);
  const [filterTag, setFilterTag] = React.useState('');
  const [selected, setSelected] = React.useState(new Set());
  const [editingTrade, setEditingTrade] = React.useState(null);

  const tagFilteredTrades = React.useMemo(() => {
    if (!filterTag.trim()) return filteredTrades;
    const tag = filterTag.trim().toLowerCase();
    return filteredTrades.filter(t => {
      const tags = `${t.tags || ''}`.toLowerCase();
      const notes = `${t.notes || ''}`.toLowerCase();
      return tags.includes(tag) || notes.includes(tag);
    });
  }, [filteredTrades, filterTag]);

  const sortedTrades = React.useMemo(() => [...tagFilteredTrades].reverse(), [tagFilteredTrades]);
  const totalPages = Math.max(1, Math.ceil(sortedTrades.length / rowsPerPage));
  const pageStart = (page - 1) * rowsPerPage;
  const pageRows = sortedTrades.slice(pageStart, pageStart + rowsPerPage);
  const [liveQuotes, setLiveQuotes] = React.useState({});
  const futuresContractMap = React.useMemo(() => ({
    GC: 'GC=F', SI: 'SI=F', CL: 'CL=F', BZ: 'BZ=F', NG: 'NG=F',
    RB: 'RB=F', HO: 'HO=F', ES: 'ES=F', NQ: 'NQ=F', YM: 'YM=F', RTY: 'RTY=F',
  }), []);

  const toQuoteSymbol = React.useCallback((trade) => {
    const assetRaw = `${trade?.asset || ''}`.toUpperCase().trim();
    if (!assetRaw) return null;
    const market = `${trade?.market || ''}`.toLowerCase();
    const asset = assetRaw.replace(/\s+/g, '');
    const underlying = asset.match(/^([A-Z]{1,6})\d{6}[CP]\d{8}$/)?.[1] || asset.split(/[-_/]/)[0];

    if (market === 'crypto' || asset.includes('USDT') || asset.includes('USD')) {
      if (asset === 'BTC' || asset === 'BTCUSD' || asset === 'BTCUSDT') return 'BTC-USD';
      if (asset === 'ETH' || asset === 'ETHUSD' || asset === 'ETHUSDT') return 'ETH-USD';
      if (asset.endsWith('USDT')) return `${asset.replace('USDT', '')}-USD`;
      if (asset.endsWith('USD')) return `${asset.replace('USD', '')}-USD`;
    }

    if (market === 'futures') {
      const key = underlying || asset;
      if (futuresContractMap[key]) return futuresContractMap[key];
      if (key.includes('GOLD')) return 'GC=F';
      if (key.includes('OIL') || key.includes('WTI')) return 'CL=F';
      if (key.includes('BRENT')) return 'BZ=F';
      if (key.includes('GAS')) return 'NG=F';
      return null;
    }

    if (market === 'options') return underlying ? underlying : null;
    if (/^[A-Z]{1,8}$/.test(asset)) return asset;
    return null;
  }, [futuresContractMap]);

  const getLiveSessionUrl = React.useCallback((trade) => {
    const tv = resolveTvSymbol ? resolveTvSymbol(trade?.asset, trade?.market) : null;
    if (!tv) return null;
    return `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(tv)}`;
  }, [resolveTvSymbol]);

  React.useEffect(() => {
    const tradeSymbols = pageRows.map((t) => toQuoteSymbol(t)).filter(Boolean);
    const quoteSymbols = [...new Set(tradeSymbols)];
    if (quoteSymbols.length === 0) { setLiveQuotes({}); return undefined; }
    let mounted = true;

    const fetchQuotes = async () => {
      try {
        const res = await fetch(`https://query1.finance.yahoo.com/v7/finance/quote?symbols=${encodeURIComponent(quoteSymbols.join(','))}`);
        const json = await res.json();
        const rows = json?.quoteResponse?.result || [];
        if (!mounted) return;
        const next = {};
        rows.forEach((q) => { next[q.symbol] = { price: toNumber(q.regularMarketPrice, NaN), changePct: toNumber(q.regularMarketChangePercent, NaN) }; });
        setLiveQuotes(next);
      } catch (_err) { if (mounted) setLiveQuotes({}); }
    };

    fetchQuotes();
    const id = setInterval(fetchQuotes, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, [pageRows, toQuoteSymbol]);

  React.useEffect(() => { setPage(1); }, [filterAsset, filterStrategy, filterTag, rowsPerPage, sortedTrades.length]);
  React.useEffect(() => { setSelected(new Set()); }, [sortedTrades.length]);

  const pageIds = pageRows.map(t => t.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelected(prev => { const next = new Set(prev); pageIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelected(prev => { const next = new Set(prev); pageIds.forEach(id => next.add(id)); return next; });
    }
  };

  const handleBulkDelete = () => {
    if (selected.size === 0) return;
    if (window.confirm(`Delete ${selected.size} selected trade${selected.size > 1 ? 's' : ''}?`)) {
      bulkDeleteTrades([...selected]);
      setSelected(new Set());
    }
  };

  const handleEditSave = async (updates) => {
    await editTrade(editingTrade.id, updates);
    setEditingTrade(null);
  };

  const cols = 'checkbox 90px 70px 120px 74px 96px 68px 92px 74px 74px 64px 64px 64px 82px 88px minmax(140px,1fr) 80px';
  const colTemplate = `24px 90px 70px 120px 74px 96px 68px 92px 74px 74px 64px 64px 64px 82px 88px minmax(140px,1fr) 80px`;

  return (
    <>
      {editingTrade && (
        <EditModal
          trade={editingTrade}
          onSave={handleEditSave}
          onClose={() => setEditingTrade(null)}
          C={C}
          inp={inp}
          cardStyle={cardStyle}
        />
      )}
      <PnLSummaryBar trades={accountTrades}/>
      <div style={{...cardStyle,padding:'12px 16px',marginBottom:'12px',display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap'}}>
        <input placeholder="🔍 Search asset..." value={filterAsset} onChange={e=>setFilterAsset(e.target.value)} style={{...inp,width:isMobile?'100%':'150px'}}/>
        {!isMobile && <>
          <select value={filterStrategy} onChange={e=>setFilterStrategy(e.target.value)} style={{...inp,width:'150px'}}>
            <option value="All">All Strategies</option>
            <option>Breakout</option><option>Scalp</option><option>Swing</option><option>Trend Following</option><option>Reversal</option>
          </select>
          <input placeholder="🏷 Filter by tag..." value={filterTag} onChange={e=>setFilterTag(e.target.value)} style={{...inp,width:'150px'}}/>
          <select value={rowsPerPage} onChange={e=>setRowsPerPage(parseInt(e.target.value, 10))} style={{...inp,width:'120px'}}>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
            <option value={250}>250 / page</option>
            <option value={500}>500 / page</option>
          </select>
        </>}
        {selected.size > 0 && (
          <button onClick={handleBulkDelete} style={{padding:'7px 14px',borderRadius:'7px',border:'none',background:'rgba(218,54,51,0.18)',color:'#f85149',cursor:'pointer',fontWeight:'700',fontSize:'12px'}}>
            🗑 Delete {selected.size} selected
          </button>
        )}
        <span style={{color:C.sub,fontSize:'12px',marginLeft:'auto',fontWeight:'600'}}>{sortedTrades.length} trades</span>
      </div>
      {isMobile ? (
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {sortedTrades.length===0&&<div style={{...cardStyle,padding:'40px',textAlign:'center',color:C.sub,fontSize:'13px'}}>No trades found.</div>}
          {pageRows.map((trade)=>{
            const tradePnl = toNumber(trade.pnl);
            const isSelected = selected.has(trade.id);
            const rr = formatTradeRr(trade);
            return (
              <div key={trade.id}
                style={{...cardStyle,padding:'14px 16px',border:`1px solid ${isSelected?C.blue:C.border}`,borderRadius:'12px',background:isSelected?'rgba(56,139,253,0.08)':C.card}}
                onClick={()=>setSelectedTradeDetail(trade)}>
                {/* Top row: asset + type badge + P&L */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'8px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                    <div onClick={e=>e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={()=>toggleSelect(trade.id)} style={{cursor:'pointer',width:'16px',height:'16px'}}/>
                    </div>
                    <span style={{fontSize:'16px',fontWeight:'900',color:C.text}}>{trade.asset}</span>
                    <span style={{background:trade.type==='Buy'?'rgba(14,203,129,0.15)':'rgba(239,68,68,0.15)',color:trade.type==='Buy'?C.greenBright:C.redBright,padding:'2px 8px',borderRadius:'20px',fontSize:'11px',fontWeight:'700'}}>{trade.type}</span>
                  </div>
                  <span style={{fontSize:'18px',fontWeight:'900',color:tradePnl>=0?C.greenBright:C.redBright}}>{tradePnl>=0?'+':''}${tradePnl.toFixed(2)}</span>
                </div>
                {/* Second row: date + strategy */}
                <div style={{display:'flex',gap:'12px',marginBottom:'8px',fontSize:'12px',color:C.sub}}>
                  <span>{trade.date}</span>
                  {trade.strategy&&<span style={{color:C.text,fontWeight:'600'}}>{trade.strategy}</span>}
                  {trade.market&&<span>{trade.market}</span>}
                </div>
                {/* Third row: entry/exit/size */}
                <div style={{display:'flex',gap:'16px',marginBottom:'8px',fontSize:'12px'}}>
                  <span style={{color:C.sub}}>Entry <span style={{color:C.text,fontWeight:'700'}}>${trade.entry}</span></span>
                  <span style={{color:C.sub}}>Exit <span style={{color:C.text,fontWeight:'700'}}>${trade.exit}</span></span>
                  <span style={{color:C.sub}}>Qty <span style={{color:C.text,fontWeight:'700'}}>{trade.size}</span></span>
                  {rr&&rr!=='—'&&<span style={{color:C.orange,fontWeight:'700'}}>{rr}</span>}
                </div>
                {/* Notes */}
                {trade.notes&&<div style={{fontSize:'12px',color:C.sub,marginBottom:'8px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{trade.notes}</div>}
                {/* Action buttons */}
                <div style={{display:'flex',gap:'8px'}} onClick={e=>e.stopPropagation()}>
                  <button onClick={()=>setEditingTrade(trade)} style={{flex:1,padding:'8px',borderRadius:'8px',border:`1px solid ${C.border}`,background:C.card2,color:C.blue,cursor:'pointer',fontWeight:'700',fontSize:'13px'}}>Edit</button>
                  <button onClick={()=>openReplay(trade.id)} style={{flex:1,padding:'8px',borderRadius:'8px',border:`1px solid ${C.border}`,background:C.card2,color:C.sub,cursor:'pointer',fontWeight:'700',fontSize:'13px'}}>Replay</button>
                  <button onClick={()=>deleteTrade(trade.id)} style={{flex:1,padding:'8px',borderRadius:'8px',border:'none',background:'rgba(239,68,68,0.12)',color:'#f85149',cursor:'pointer',fontWeight:'700',fontSize:'13px'}}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{...cardStyle,overflowX:'auto'}}>
          <div style={{minWidth:'1400px'}}>
            <div style={{display:'grid',gridTemplateColumns:colTemplate,gap:'8px',padding:'10px 16px',background:C.card2,borderBottom:`1px solid ${C.border}`,color:C.sub,fontSize:'10px',textTransform:'uppercase',letterSpacing:'0.5px',fontWeight:'700',alignItems:'center'}}>
              <div><input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll} style={{cursor:'pointer'}}/></div>
              {['Date','Asset','Live Mkt','Market','Live Session','Type','Strategy','Entry','Exit','Size','Stop','Target','R:R','P&L','Notes','Actions'].map(h=><div key={h}>{h}</div>)}
            </div>
            {sortedTrades.length===0&&<div style={{padding:'40px',textAlign:'center',color:C.sub,fontSize:'13px'}}>No trades found.</div>}
            {pageRows.map((trade,i)=>(
              (() => {
                const tradePnl = toNumber(trade.pnl);
                const quoteSymbol = toQuoteSymbol(trade);
                const quote = quoteSymbol ? liveQuotes[quoteSymbol] : null;
                const hasQuote = quote && !Number.isNaN(quote.price);
                const liveSessionUrl = getLiveSessionUrl(trade);
                const isSelected = selected.has(trade.id);
                return (
                  <div key={trade.id}
                    style={{display:'grid',gridTemplateColumns:colTemplate,gap:'8px',padding:'12px 16px',borderBottom:`1px solid ${C.border}`,fontSize:'11px',alignItems:'center',background:isSelected?'rgba(56,139,253,0.1)':i%2===0?C.card:C.card2}}
                    onClick={()=>setSelectedTradeDetail(trade)}
                    onMouseEnter={e=>{ if (!isSelected) e.currentTarget.style.background='rgba(56,139,253,0.08)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background=isSelected?'rgba(56,139,253,0.1)':i%2===0?C.card:C.card2; }}>
                    <div onClick={e=>e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={()=>toggleSelect(trade.id)} style={{cursor:'pointer'}}/>
                    </div>
                    <div style={{color:C.sub}}>{trade.date}</div>
                    <div style={{fontWeight:'800',color:C.text}}>{trade.asset}</div>
                    <div style={{color:hasQuote?(quote.changePct>=0?C.greenBright:C.redBright):C.sub,fontWeight:'700',fontSize:'10px'}}>
                      {hasQuote?(<>${quote.price.toFixed(2)}{!Number.isNaN(quote.changePct)?` (${quote.changePct>=0?'+':''}${quote.changePct.toFixed(2)}%)`:'%'}</>):'—'}
                    </div>
                    <div style={{color:C.sub,fontSize:'10px'}}>{trade.market||'—'}</div>
                    <div>
                      {liveSessionUrl?(
                        <a href={liveSessionUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()}
                          style={{display:'inline-block',padding:'3px 8px',borderRadius:'12px',border:`1px solid ${C.blue}66`,background:'rgba(56,139,253,0.14)',color:C.blueBright,fontSize:'10px',fontWeight:'700',textDecoration:'none'}}>
                          Join Live
                        </a>
                      ):<span style={{color:C.sub,fontSize:'10px'}}>—</span>}
                    </div>
                    <div><span style={{background:trade.type==='Buy'?'rgba(46,160,67,0.2)':'rgba(218,54,51,0.2)',color:trade.type==='Buy'?C.greenBright:C.redBright,padding:'2px 8px',borderRadius:'20px',fontSize:'10px',fontWeight:'700'}}>{trade.type}</span></div>
                    <div style={{color:C.sub,fontSize:'11px'}}>{trade.strategy||'—'}</div>
                    <div style={{fontWeight:'600',color:C.text}}>${trade.entry}</div>
                    <div style={{fontWeight:'600',color:C.text}}>${trade.exit}</div>
                    <div style={{color:C.sub}}>{trade.size}</div>
                    <div style={{color:C.sub}}>{trade.stopLoss?`$${trade.stopLoss}`:'—'}</div>
                    <div style={{color:C.sub}}>{trade.target?`$${trade.target}`:'—'}</div>
                    <div style={{color:C.orange,fontWeight:'700',whiteSpace:'nowrap'}}>{formatTradeRr(trade)}</div>
                    <div style={{color:tradePnl>=0?C.greenBright:C.redBright,fontWeight:'800',fontSize:'13px'}}>{tradePnl>=0?'+':''}${tradePnl.toFixed(2)}</div>
                    <div style={{color:C.sub,fontSize:'11px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{trade.notes||''}</div>
                    <div style={{display:'flex',gap:'4px'}} onClick={e=>e.stopPropagation()}>
                      <button onClick={()=>setEditingTrade(trade)} style={{background:C.card2,border:`1px solid ${C.border}`,color:C.blue,cursor:'pointer',borderRadius:'4px',padding:'4px 7px',fontSize:'11px'}} title="Edit">✏️</button>
                      <button onClick={()=>openReplay(trade.id)} style={{background:C.card2,border:`1px solid ${C.border}`,color:C.blue,cursor:'pointer',borderRadius:'4px',padding:'4px 7px',fontSize:'11px'}} title="Replay">▶</button>
                      <button onClick={()=>deleteTrade(trade.id)} style={{background:C.card2,border:`1px solid ${C.border}`,color:C.red,cursor:'pointer',borderRadius:'4px',padding:'4px 7px',fontSize:'11px'}} title="Delete">🗑</button>
                    </div>
                  </div>
                );
              })()
            ))}
          </div>
        </div>
      )}
      {sortedTrades.length > 0 && (
        <div style={{...cardStyle,padding:'10px 14px',marginTop:'10px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',flexWrap:'wrap'}}>
          <div style={{color:C.sub,fontSize:'12px'}}>
            Showing {pageStart+1}–{Math.min(pageStart+rowsPerPage,sortedTrades.length)} of {sortedTrades.length}
            {selected.size>0&&<span style={{color:C.blue,marginLeft:'10px'}}>{selected.size} selected</span>}
          </div>
          <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
            <button disabled={page===1} onClick={()=>setPage(1)} style={{padding:'6px 10px',borderRadius:'6px',border:`1px solid ${C.border}`,background:C.card2,color:C.text,cursor:page===1?'not-allowed':'pointer',opacity:page===1?0.5:1}}>First</button>
            <button disabled={page===1} onClick={()=>setPage(p=>Math.max(1,p-1))} style={{padding:'6px 10px',borderRadius:'6px',border:`1px solid ${C.border}`,background:C.card2,color:C.text,cursor:page===1?'not-allowed':'pointer',opacity:page===1?0.5:1}}>Prev</button>
            <span style={{color:C.sub,fontSize:'12px'}}>Page {page} / {totalPages}</span>
            <button disabled={page===totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))} style={{padding:'6px 10px',borderRadius:'6px',border:`1px solid ${C.border}`,background:C.card2,color:C.text,cursor:page===totalPages?'not-allowed':'pointer',opacity:page===totalPages?0.5:1}}>Next</button>
            <button disabled={page===totalPages} onClick={()=>setPage(totalPages)} style={{padding:'6px 10px',borderRadius:'6px',border:`1px solid ${C.border}`,background:C.card2,color:C.text,cursor:page===totalPages?'not-allowed':'pointer',opacity:page===totalPages?0.5:1}}>Last</button>
          </div>
        </div>
      )}
    </>
  );
}
